import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, Edit, Plus, Trash2, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import ExpenseForm from "./ExpenseForm";
import { toast } from "sonner";

export default function BudgetTracking({ project, tasks, timeEntries, onUpdateBudget, invoices = [] }) {
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const queryClient = useQueryClient();

  const hourlyRate = project.hourly_rate || 2500;
  const budget = project.budget || 0;

  const { data: expenses = [] } = useQuery({
    queryKey: ["project-expenses", project.id],
    queryFn: () => base44.entities.ProjectExpense.filter({ project_id: project.id }),
  });

  const createExpenseMutation = useMutation({
    mutationFn: (data) => base44.entities.ProjectExpense.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-expenses"] });
      setShowExpenseForm(false);
      setEditingExpense(null);
      toast.success("Utgift opprettet");
    },
  });

  const updateExpenseMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ProjectExpense.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-expenses"] });
      setShowExpenseForm(false);
      setEditingExpense(null);
      toast.success("Utgift oppdatert");
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id) => base44.entities.ProjectExpense.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-expenses"] });
      toast.success("Utgift slettet");
    },
  });

  const handleExpenseSubmit = (data) => {
    if (editingExpense) {
      updateExpenseMutation.mutate({ id: editingExpense.id, data });
    } else {
      createExpenseMutation.mutate(data);
    }
  };

  // Beregn faktisk kostnad basert på tidsregistreringer
  const totalHoursSpent = timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
  const laborCostFromTime = totalHoursSpent * hourlyRate;

  // Beregn totale utgifter fra registrerte kostnader
  const totalExpensesEstimated = expenses.reduce((sum, e) => sum + (e.estimated_amount || 0), 0);
  const totalExpensesActual = expenses.reduce((sum, e) => sum + (e.actual_amount || 0), 0);

  // Total faktisk kostnad (lønn fra timer + faktiske utgifter)
  const actualCost = laborCostFromTime + totalExpensesActual;

  // Beregn estimert gjenstående kostnad
  const incompleteTasks = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled");
  const estimatedHoursRemaining = incompleteTasks.reduce((sum, task) => {
    if (!task.estimated_hours) return sum;
    const taskTimeSpent = timeEntries
      .filter(e => e.task_id === task.id)
      .reduce((s, e) => s + (e.duration_minutes || 0) / 60, 0);
    return sum + Math.max(0, task.estimated_hours - taskTimeSpent);
  }, 0);
  const estimatedRemainingLabor = estimatedHoursRemaining * hourlyRate;

  // Estimert gjenstående fra planlagte utgifter (estimated - actual)
  const estimatedRemainingExpenses = expenses.reduce((sum, e) => {
    const remaining = (e.estimated_amount || 0) - (e.actual_amount || 0);
    return sum + Math.max(0, remaining);
  }, 0);

  const estimatedRemainingCost = estimatedRemainingLabor + estimatedRemainingExpenses;

  // Beregn total forventet kostnad
  const totalEstimatedCost = actualCost + estimatedRemainingCost;

  // Beregn budsjettavvik
  const budgetVariance = budget - totalEstimatedCost;
  const budgetVariancePercent = budget > 0 ? (budgetVariance / budget) * 100 : 0;
  const isOverBudget = budgetVariance < 0;
  const isAtRisk = budgetVariancePercent < 10 && budgetVariancePercent > -10;

  // Beregn framdrift
  const budgetUsedPercent = budget > 0 ? (actualCost / budget) * 100 : 0;
  const estimatedUsagePercent = budget > 0 ? (totalEstimatedCost / budget) * 100 : 0;

  // Gjennomsnittlig timepris faktisk brukt
  const avgHourlyRate = totalHoursSpent > 0 ? laborCostFromTime / totalHoursSpent : hourlyRate;

  // Gruppér utgifter etter type
  const expenseTypes = {
    labor: { label: "Lønn", color: "indigo" },
    materials: { label: "Materialer", color: "blue" },
    travel: { label: "Reise", color: "purple" },
    equipment: { label: "Utstyr", color: "emerald" },
    software: { label: "Programvare", color: "cyan" },
    consulting: { label: "Konsulenter", color: "amber" },
    other: { label: "Annet", color: "slate" },
  };

  const expensesByType = Object.keys(expenseTypes).map(type => {
    const typeExpenses = expenses.filter(e => e.expense_type === type);
    const estimated = typeExpenses.reduce((sum, e) => sum + (e.estimated_amount || 0), 0);
    const actual = typeExpenses.reduce((sum, e) => sum + (e.actual_amount || 0), 0);
    return {
      type,
      ...expenseTypes[type],
      expenses: typeExpenses,
      estimated,
      actual,
      count: typeExpenses.length,
    };
  }).filter(t => t.count > 0);

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Budsjett & Kostnader</h3>
            <p className="text-sm text-slate-500">Økonomisk oversikt</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowExpenseForm(true)}
          >
            <Plus className="w-3 h-3 mr-2" />
            Ny Utgift
          </Button>
          {onUpdateBudget && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onUpdateBudget}
            >
              <Edit className="w-3 h-3 mr-2" />
              Rediger Budsjett
            </Button>
          )}
        </div>
      </div>

      {/* Status Alert */}
      {budget > 0 && (
        <>
          {isOverBudget ? (
            <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-rose-900">Over budsjett</p>
                <p className="text-xs text-rose-700 mt-1">
                  Prosjektet forventes å overstige budsjettet med {Math.abs(budgetVariance).toLocaleString()} kr
                </p>
              </div>
            </div>
          ) : isAtRisk && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">Budsjettrisiko</p>
                <p className="text-xs text-amber-700 mt-1">
                  Prosjektet nærmer seg budsjettgrensen
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Hovedtall */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
          <p className="text-sm text-slate-600 mb-1">Budsjett</p>
          <p className="text-3xl font-bold text-emerald-700">
            {budget > 0 ? budget.toLocaleString() : "Ikke satt"} kr
          </p>
          <p className="text-xs text-slate-600 mt-2">
            {hourlyRate.toLocaleString()} kr/time
          </p>
        </div>

        <div className={cn(
          "p-4 rounded-xl border",
          isOverBudget ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"
        )}>
          <p className="text-sm text-slate-600 mb-1">Forventet totalkostnad</p>
          <p className={cn(
            "text-3xl font-bold",
            isOverBudget ? "text-rose-700" : "text-slate-900"
          )}>
            {totalEstimatedCost.toLocaleString()} kr
          </p>
          <p className="text-xs text-slate-600 mt-2">
            {(totalHoursSpent + estimatedHoursRemaining).toFixed(1)} timer totalt
          </p>
        </div>
      </div>

      {/* Faktureringsstatus */}
      {invoices.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-50 rounded-xl border border-green-200">
            <p className="text-xs text-slate-600 mb-1">Fakturert</p>
            <p className="text-xl font-bold text-green-700">
              {invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString()} kr
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {invoices.filter(inv => inv.status === 'paid').length} fakturaer betalt
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
            <p className="text-xs text-slate-600 mb-1">Til fakturering</p>
            <p className="text-xl font-bold text-orange-700">
              {invoices.filter(inv => inv.status === 'draft' || inv.status === 'sent').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString()} kr
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {invoices.filter(inv => inv.status === 'draft' || inv.status === 'sent').length} faktura(er)
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-xs text-slate-600 mb-1">Ufakturert arbeid</p>
            <p className="text-xl font-bold text-blue-700">
              {(actualCost - invoices.reduce((sum, inv) => sum + inv.total_amount, 0)).toLocaleString()} kr
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {(totalHoursSpent - invoices.reduce((sum, inv) => sum + (inv.total_hours || 0), 0)).toFixed(1)} timer
            </p>
          </div>
        </div>
      )}

      {/* Detaljert kostnadsoversikt */}
      <div className="space-y-4 mb-6">
        <div className="p-4 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-900">Faktisk kostnad (brukt)</p>
            <p className="text-lg font-bold text-slate-900">{actualCost.toLocaleString()} kr</p>
          </div>
          <div className="space-y-1 mb-2">
            <p className="text-xs text-slate-500">
              💼 Lønn fra timer: {laborCostFromTime.toLocaleString()} kr ({totalHoursSpent.toFixed(1)}t)
            </p>
            <p className="text-xs text-slate-500">
              💰 Andre utgifter: {totalExpensesActual.toLocaleString()} kr
            </p>
          </div>
          {budget > 0 && (
            <>
              <Progress value={budgetUsedPercent} className="h-2 mb-1" />
              <p className="text-xs text-slate-500 text-right">
                {budgetUsedPercent.toFixed(1)}% av budsjett
              </p>
            </>
          )}
        </div>

        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-900">Estimert gjenstående</p>
            <p className="text-lg font-bold text-amber-600">{estimatedRemainingCost.toLocaleString()} kr</p>
          </div>
          <div className="space-y-1 mb-2">
            <p className="text-xs text-slate-500">
              💼 Lønn gjenstående: {estimatedRemainingLabor.toLocaleString()} kr ({estimatedHoursRemaining.toFixed(1)}t)
            </p>
            <p className="text-xs text-slate-500">
              💰 Utgifter gjenstående: {estimatedRemainingExpenses.toLocaleString()} kr
            </p>
          </div>
          {budget > 0 && (
            <>
              <Progress 
                value={estimatedUsagePercent} 
                className={cn(
                  "h-2 mb-1",
                  estimatedUsagePercent > 100 && "[&>div]:bg-rose-500"
                )}
              />
              <p className={cn(
                "text-xs text-right",
                estimatedUsagePercent > 100 ? "text-rose-600" : "text-slate-500"
              )}>
                {estimatedUsagePercent.toFixed(1)}% av budsjett
              </p>
            </>
          )}
        </div>
      </div>

      {/* Utgifter etter type */}
      {expensesByType.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Utgifter etter kategori</h4>
          <div className="space-y-3">
            {expensesByType.map((typeGroup) => (
              <div key={typeGroup.type} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedType(expandedType === typeGroup.type ? null : typeGroup.type)}
                  className="w-full p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Receipt className={`w-5 h-5 text-${typeGroup.color}-600`} />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{typeGroup.label}</p>
                        <p className="text-xs text-slate-500">{typeGroup.count} utgift{typeGroup.count > 1 ? 'er' : ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {typeGroup.actual.toLocaleString()} kr
                      </p>
                      {typeGroup.estimated > 0 && (
                        <p className="text-xs text-slate-500">
                          av {typeGroup.estimated.toLocaleString()} kr est.
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                
                {expandedType === typeGroup.type && (
                  <div className="p-4 bg-white space-y-2 border-t border-slate-200">
                    {typeGroup.expenses.map((expense) => (
                      <div key={expense.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{expense.category}</p>
                          {expense.description && (
                            <p className="text-xs text-slate-600 mt-1">{expense.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {expense.status === "planned" ? "Planlagt" :
                               expense.status === "committed" ? "Forpliktet" :
                               expense.status === "invoiced" ? "Fakturert" : "Betalt"}
                            </Badge>
                            {expense.expense_date && (
                              <span className="text-xs text-slate-500">{expense.expense_date}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="text-right">
                            {expense.actual_amount > 0 && (
                              <p className="text-sm font-semibold text-slate-900">
                                {expense.actual_amount.toLocaleString()} kr
                              </p>
                            )}
                            {expense.estimated_amount > 0 && (
                              <p className="text-xs text-slate-500">
                                Est: {expense.estimated_amount.toLocaleString()} kr
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingExpense(expense);
                              setShowExpenseForm(true);
                            }}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              if (confirm("Er du sikker på at du vil slette denne utgiften?")) {
                                deleteExpenseMutation.mutate(expense.id);
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3 text-slate-400 hover:text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budsjettavvik */}
      {budget > 0 && (
        <div className={cn(
          "p-4 rounded-xl border",
          budgetVariance >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {budgetVariance >= 0 ? (
                <TrendingDown className="w-5 h-5 text-emerald-600" />
              ) : (
                <TrendingUp className="w-5 h-5 text-rose-600" />
              )}
              <div>
                <p className="text-sm font-medium text-slate-900">Budsjettavvik</p>
                <p className="text-xs text-slate-500">Forventet vs budsjett</p>
              </div>
            </div>
            <div className="text-right">
              <p className={cn(
                "text-2xl font-bold",
                budgetVariance >= 0 ? "text-emerald-600" : "text-rose-600"
              )}>
                {budgetVariance >= 0 ? "+" : ""}{budgetVariance.toLocaleString()} kr
              </p>
              <p className={cn(
                "text-xs",
                budgetVariance >= 0 ? "text-emerald-600" : "text-rose-600"
              )}>
                {budgetVariance >= 0 ? "+" : ""}{budgetVariancePercent.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {!budget && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700">
            💡 Sett et budsjett for prosjektet for å spore kostnader og avvik
          </p>
        </div>
      )}

      {budget > 0 && incompleteTasks.filter(t => !t.estimated_hours).length > 0 && (
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-700">
            ⚠️ {incompleteTasks.filter(t => !t.estimated_hours).length} oppgaver mangler estimert tidsbruk
          </p>
        </div>
      )}

      {showExpenseForm && (
        <ExpenseForm
          expense={editingExpense}
          projectId={project.id}
          projectName={project.name}
          onSubmit={handleExpenseSubmit}
          onClose={() => {
            setShowExpenseForm(false);
            setEditingExpense(null);
          }}
          isLoading={createExpenseMutation.isPending || updateExpenseMutation.isPending}
        />
      )}
    </Card>
  );
}